<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
    modelValue: {
        type: [String, Array],
        default: '',
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    minDate: {
        type: String,
        default: '',
    },
    maxDate: {
        type: String,
        default: '',
    },
})

const emit = defineEmits(['update:modelValue'])

// ─── Helpers ────────────────────────────────────────────────────────────────

const getIsoDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const normalizeIsoDate = (raw) => {
    const value = String(raw || '').trim()
    if (!value) return ''
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return ''

    return `${match[1]}-${match[2]}-${match[3]}`
}

const parseIsoDate = (iso) => {
    const normalized = normalizeIsoDate(iso)
    if (!normalized) return null
    const [year, month, day] = normalized.split('-').map(Number)
    if (!year || !month || !day) return null
    const date = new Date(year, month - 1, day)
    date.setHours(0, 0, 0, 0)
    return date
}

// ─── Selected state ──────────────────────────────────────────────────────────

const selectedSet = computed(() => {
    if (props.multiple) {
        const values = Array.isArray(props.modelValue) ? props.modelValue : []
        return new Set(
            values
                .map((value) => normalizeIsoDate(value))
                .filter(Boolean)
        )
    }
    const normalized = normalizeIsoDate(props.modelValue)
    return new Set(normalized ? [normalized] : [])
})

const baseDate = computed(() => {
    if (props.multiple) {
        const first = Array.isArray(props.modelValue) ? props.modelValue[0] : null
        return parseIsoDate(first) || new Date()
    }
    return parseIsoDate(props.modelValue) || new Date()
})

const visibleMonth = ref(baseDate.value.getMonth())
const visibleYear = ref(baseDate.value.getFullYear())

watch(baseDate, (next) => {
    visibleMonth.value = next.getMonth()
    visibleYear.value = next.getFullYear()
})

// ─── View mode: 'days' | 'months' | 'years' ──────────────────────────────────

const viewMode = ref('days')

// ─── Min / max helpers ───────────────────────────────────────────────────────

const minDateObj = computed(() => parseIsoDate(props.minDate))
const maxDateObj = computed(() => parseIsoDate(props.maxDate))

const isDisabled = (date) => {
    if (minDateObj.value && date < minDateObj.value) return true
    if (maxDateObj.value && date > maxDateObj.value) return true
    return false
}

// ─── Day view ────────────────────────────────────────────────────────────────

const monthLabel = computed(() => {
    const labelDate = new Date(visibleYear.value, visibleMonth.value, 1)
    return labelDate.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })
})

const weekdays = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo']

const calendarDays = computed(() => {
    const firstOfMonth = new Date(visibleYear.value, visibleMonth.value, 1)
    firstOfMonth.setHours(0, 0, 0, 0)

    const mondayIndex = (firstOfMonth.getDay() + 6) % 7
    const gridStart = new Date(firstOfMonth)
    gridStart.setDate(firstOfMonth.getDate() - mondayIndex)

    return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(gridStart)
        date.setDate(gridStart.getDate() + index)
        date.setHours(0, 0, 0, 0)

        const iso = getIsoDate(date)
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        return {
            iso,
            day: date.getDate(),
            inCurrentMonth: date.getMonth() === visibleMonth.value,
            selected: selectedSet.value.has(iso),
            isToday: getIsoDate(today) === iso,
            disabled: isDisabled(date),
        }
    })
})

const goMonth = (delta) => {
    const next = new Date(visibleYear.value, visibleMonth.value + delta, 1)
    visibleMonth.value = next.getMonth()
    visibleYear.value = next.getFullYear()
}

const toggleDate = (iso, disabled) => {
    if (disabled) return

    if (props.multiple) {
        const values = new Set(Array.isArray(props.modelValue) ? props.modelValue : [])
        if (values.has(iso)) {
            values.delete(iso)
        } else {
            values.add(iso)
        }
        emit('update:modelValue', [...values].sort())
        return
    }

    emit('update:modelValue', iso)
}

// "Vandaag" shortcut
const todayIso = getIsoDate(new Date())
const todayDisabled = computed(() => isDisabled(new Date()))

const goToday = () => {
    const today = new Date()
    visibleMonth.value = today.getMonth()
    visibleYear.value = today.getFullYear()
    if (!todayDisabled.value && !props.multiple) {
        emit('update:modelValue', todayIso)
    }
}

// ─── Month view ──────────────────────────────────────────────────────────────

const monthNames = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']

const selectMonth = (monthIndex) => {
    visibleMonth.value = monthIndex
    viewMode.value = 'days'
}

// ─── Year view ───────────────────────────────────────────────────────────────

// Show a window of years centered around visibleYear
const yearWindowStart = ref(Math.floor(visibleYear.value / 12) * 12)

const yearGrid = computed(() => {
    return Array.from({ length: 12 }, (_, i) => yearWindowStart.value + i)
})

const goYearWindow = (delta) => {
    yearWindowStart.value += delta * 12
}

const selectYear = (year) => {
    visibleYear.value = year
    viewMode.value = 'months'
}

const openYearView = () => {
    yearWindowStart.value = Math.floor(visibleYear.value / 12) * 12
    viewMode.value = 'years'
}
</script>

<template>
    <div class="calendar-picker">
        <!-- ── Day view ──────────────────────────────────────────── -->
        <template v-if="viewMode === 'days'">
            <div class="calendar-header">
                <button type="button" class="calendar-nav" @click="goMonth(-1)">‹</button>
                <button type="button" class="calendar-month-label" title="Klik om jaar te kiezen" @click="openYearView">
                    {{ monthLabel }}
                    <svg class="calendar-month-label-chevron" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6l4 4 4-4"/></svg>
                </button>
                <button type="button" class="calendar-nav" @click="goMonth(1)">›</button>
            </div>

            <div class="calendar-weekdays">
                <span v-for="day in weekdays" :key="day">{{ day }}</span>
            </div>

            <div class="calendar-grid">
                <button
                    v-for="day in calendarDays"
                    :key="day.iso"
                    type="button"
                    class="calendar-day"
                    :class="{
                        'calendar-day-outside': !day.inCurrentMonth,
                        'calendar-day-selected': day.selected,
                        'calendar-day-today': day.isToday,
                        'calendar-day-disabled': day.disabled,
                    }"
                    :disabled="day.disabled"
                    @click="toggleDate(day.iso, day.disabled)"
                >
                    {{ day.day }}
                </button>
            </div>

            <div class="calendar-footer">
                <button
                    type="button"
                    class="calendar-today-btn"
                    :class="{ 'calendar-today-btn-disabled': todayDisabled }"
                    :disabled="todayDisabled"
                    @click="goToday"
                >
                    Vandaag
                </button>
            </div>
        </template>

        <!-- ── Year view ─────────────────────────────────────────── -->
        <template v-else-if="viewMode === 'years'">
            <div class="calendar-header">
                <button type="button" class="calendar-nav" @click="goYearWindow(-1)">‹</button>
                <span class="calendar-month-label calendar-month-label-static">
                    {{ yearWindowStart }}–{{ yearWindowStart + 11 }}
                </span>
                <button type="button" class="calendar-nav" @click="goYearWindow(1)">›</button>
            </div>

            <div class="calendar-year-grid">
                <button
                    v-for="year in yearGrid"
                    :key="year"
                    type="button"
                    class="calendar-year-btn"
                    :class="{ 'calendar-year-selected': year === visibleYear }"
                    @click="selectYear(year)"
                >
                    {{ year }}
                </button>
            </div>
        </template>

        <!-- ── Month view ─────────────────────────────────────────── -->
        <template v-else-if="viewMode === 'months'">
            <div class="calendar-header">
                <button type="button" class="calendar-nav" @click="visibleYear--">‹</button>
                <button type="button" class="calendar-month-label" title="Klik om jaar te kiezen" @click="openYearView">
                    {{ visibleYear }}
                    <svg class="calendar-month-label-chevron" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6l4 4 4-4"/></svg>
                </button>
                <button type="button" class="calendar-nav" @click="visibleYear++">›</button>
            </div>

            <div class="calendar-month-grid">
                <button
                    v-for="(name, idx) in monthNames"
                    :key="idx"
                    type="button"
                    class="calendar-month-btn"
                    :class="{ 'calendar-month-selected': idx === visibleMonth }"
                    @click="selectMonth(idx)"
                >
                    {{ name }}
                </button>
            </div>
        </template>
    </div>
</template>

<style scoped>
.calendar-picker {
    width: 100%;
    box-sizing: border-box;
    padding: 12px;
    border: 1px solid rgba(var(--coral-rgb), 0.2);
    border-radius: 14px;
    background: var(--white);
}

.calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-1);
    margin-bottom: 10px;
}

.calendar-month-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: 1px solid rgba(var(--coral-rgb), 0.25);
    border-radius: var(--radius-sm);
    background: none;
    color: var(--ink);
    cursor: pointer;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    text-transform: capitalize;
    transition: background 0.15s, border-color 0.15s;
}

.calendar-month-label:hover {
    border-color: rgba(var(--coral-rgb), 0.5);
    background: rgba(var(--coral-rgb), 0.08);
}

.calendar-month-label-chevron {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    color: var(--coral);
    opacity: 0.7;
}

.calendar-month-label-static {
    border-color: transparent;
    cursor: default;
}

.calendar-month-label-static:hover {
    border-color: transparent;
    background: none;
}

.calendar-nav {
    width: 32px;
    height: 32px;
    border: 1px solid rgba(var(--coral-rgb), 0.25);
    border-radius: var(--radius-full);
    background: var(--white);
    color: var(--ink);
    cursor: pointer;
    font: inherit;
    font-size: 1.1rem;
    line-height: 1;
}

.calendar-nav:hover {
    border-color: rgba(var(--coral-rgb), 0.45);
    background: rgba(var(--coral-rgb), 0.06);
}

.calendar-weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 8px;
    color: var(--muted);
    font-size: 0.75rem;
    text-align: center;
    text-transform: uppercase;
}

.calendar-grid,
.calendar-year-grid,
.calendar-month-grid {
    display: grid;
    gap: var(--sp-1);
}

.calendar-grid {
    grid-template-columns: repeat(7, 1fr);
}

.calendar-year-grid,
.calendar-month-grid {
    grid-template-columns: repeat(4, 1fr);
    margin-bottom: 4px;
}

.calendar-day,
.calendar-year-btn,
.calendar-month-btn {
    border: 1px solid transparent;
    border-radius: 10px;
    background: var(--white);
    color: var(--ink);
    cursor: pointer;
    font: inherit;
    font-size: 0.85rem;
    transition: border-color 0.15s, background 0.15s, color 0.15s, opacity 0.15s;
}

.calendar-day {
    height: 34px;
}

.calendar-year-btn,
.calendar-month-btn {
    padding: 8px 4px;
    font-weight: 500;
}

.calendar-month-btn {
    padding: 10px 4px;
    text-transform: capitalize;
}

.calendar-day:hover,
.calendar-year-btn:hover,
.calendar-month-btn:hover {
    border-color: rgba(var(--coral-rgb), 0.35);
    background: rgba(var(--coral-rgb), 0.08);
}

.calendar-day-selected,
.calendar-year-selected,
.calendar-month-selected,
.calendar-day-selected:hover,
.calendar-year-selected:hover,
.calendar-month-selected:hover {
    border-color: rgba(var(--coral-rgb), 0.5);
    background: linear-gradient(135deg, var(--coral), var(--coral-light));
    color: var(--white);
}

.calendar-day-today {
    box-shadow: inset 0 0 0 1px rgba(var(--mint-rgb), 0.8);
}

.calendar-day-outside {
    color: var(--muted);
    opacity: 0.6;
}

.calendar-day-disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.calendar-day-disabled:hover {
    border-color: transparent;
    background: var(--white);
}

.calendar-footer {
    display: flex;
    justify-content: center;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid rgba(var(--coral-rgb), 0.12);
}

.calendar-today-btn {
    padding: 4px 12px;
    border: none;
    border-radius: var(--radius-sm);
    background: none;
    color: var(--coral);
    cursor: pointer;
    font: inherit;
    font-size: 0.8rem;
    font-weight: 600;
}

.calendar-today-btn:hover {
    background: rgba(var(--coral-rgb), 0.08);
}

.calendar-today-btn-disabled,
.calendar-today-btn-disabled:hover {
    background: none;
    color: var(--muted);
    cursor: not-allowed;
}

@media (max-width: 400px) {
    .calendar-picker {
        padding: 8px;
        border-radius: 10px;
    }

    .calendar-nav {
        width: 26px;
        height: 26px;
        font-size: 0.9rem;
    }

    .calendar-month-label {
        padding: 3px 6px;
        font-size: 0.85rem;
    }

    .calendar-day {
        height: 28px;
        border-radius: 7px;
        font-size: 0.75rem;
    }

    .calendar-grid,
    .calendar-year-grid,
    .calendar-month-grid {
        gap: 4px;
    }
}
</style>
